import { CreditCardIcon } from "@heroicons/react/16/solid";
import { NavLink } from "react-router-dom";

import Hint from "~/components/Hint";
import { QUOTA_DETAILS_PATH_WITH_ORIGIN } from "~/constants/appRoutes.ts";
import type { ProjectRevenueQuota } from "~/modules/project/api/projectRevenueReport/projectRevenueReportTypes.ts";
import { getQuotaConsumedCents } from "~/modules/project/components/ProjectDetailsView/components/ProjectRevenueReportSection/projectRevenueReportUtils.ts";
import { validQuotaDetailsNavigatedFromOrigins } from "~/modules/quota/components/QuotaDetailsView/quotaDetailsUtils.ts";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";

// The invoices of a quota count as revenue only on their own project. When an invoice of the quota
// sits on another project, the revenue of this project does not contain its money.
function getOtherProjectsHint(quota: ProjectRevenueQuota, projectId: string): string | null {
	const invoicesOnOtherProjects = quota.invoices.filter((invoice) => invoice.projectId !== projectId);
	if (invoicesOnOtherProjects.length === 0) return null;
	if (invoicesOnOtherProjects.length === quota.invoices.length) {
		return "Das Kontingent beruht auf Rechnungen anderer Projekte.";
	}
	return "Das Kontingent beruht u.a. auf Rechnungen anderer Projekte.";
}

type Props = {
	quota: ProjectRevenueQuota;
	projectId: string;
};

export const ProjectRevenueQuotaCard = ({ quota, projectId }: Props) => {
	const quotaPath = QUOTA_DETAILS_PATH_WITH_ORIGIN(quota.companyId,
		quota.quotaId,
		validQuotaDetailsNavigatedFromOrigins.PROJECT,
		{ projectId });
	const otherProjectsHint = getOtherProjectsHint(quota, projectId);

	return <div className="flex flex-col gap-y-2 rounded-md border border-gray-200 p-3 text-sm">
		<NavLink to={quotaPath}
				 className="flex items-center gap-x-1 font-medium text-gray-800 hover:text-primary-500 hover:underline">
			<CreditCardIcon className="size-4 shrink-0 text-gray-400" />
			{quota.title}
		</NavLink>
		<div className="flex items-baseline justify-between gap-x-3">
			<span className="text-gray-500">Verfügt auf diesem Projekt</span>
			<span className="font-semibold text-gray-900">{formatCentsToCurrency(getQuotaConsumedCents(quota))}</span>
		</div>
		{otherProjectsHint && <Hint size="xs">{otherProjectsHint}</Hint>}
	</div>;
};
